import React from "react";

/**
 * Shared presentation primitives for the public (unauthenticated) site.
 * Kept deliberately small: the public pages are long-form editorial content,
 * so the value is in consistent rhythm, not in a component framework.
 */

export const Mark: React.FC<{ className?: string }> = ({ className = "h-9 w-9" }) => (
  <svg viewBox="0 0 64 64" className={className} role="img" aria-label="Nafnleyndarkerfi">
    <rect width="64" height="64" rx="14" fill="#0F2E2E" />
    <path fill="#5EEAD4" d="M16 52c0-9 7.2-15 16-15s16 6 16 15z" />
    <circle cx="32" cy="24" r="11" fill="#14B8A6" />
    <g stroke="#fff" strokeWidth="2.8" strokeLinecap="round">
      <path d="M32 17v14" />
      <path d="M25.9 20.5l12.2 7" />
      <path d="M38.1 20.5l-12.2 7" />
    </g>
  </svg>
);

/**
 * Link to a page outside the site. Opens in a new tab; `rel` set so the target
 * cannot touch this window. Styling comes from .site-prose for links in body
 * copy, so this only adds the arrow marker.
 */
export const Ext: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
    <span aria-hidden="true" className="ml-0.5 text-[0.7em] align-super">
      ↗
    </span>
  </a>
);

/** mailto link, so the address is written once per page and looks deliberate. */
export const Mail: React.FC<{ address: string; subject?: string }> = ({ address, subject }) => (
  <a href={`mailto:${address}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`}>
    {address}
  </a>
);

/** Small uppercase label above a heading. */
export const Kicker: React.FC<{ children: React.ReactNode; tone?: "light" | "dark" }> = ({
  children,
  tone = "dark",
}) => (
  <p
    className={`text-xs font-semibold uppercase tracking-[0.16em] ${
      tone === "light" ? "text-accent-light" : "text-accent"
    }`}
  >
    {children}
  </p>
);

/** Page title block used at the top of every public page. */
export const PageHeader: React.FC<{
  kicker?: string;
  title: string;
  lead?: React.ReactNode;
}> = ({ kicker, title, lead }) => (
  <header className="max-w-3xl">
    {kicker && <Kicker>{kicker}</Kicker>}
    <h1 className="font-display text-4xl sm:text-5xl leading-[1.1] text-ink mt-3">{title}</h1>
    {lead && <div className="mt-6 text-lg leading-relaxed text-ink-600">{lead}</div>}
  </header>
);

/** A numbered long-form section with an anchor target. */
export const Section: React.FC<{
  id: string;
  title: string;
  number?: string;
  children: React.ReactNode;
}> = ({ id, title, number, children }) => (
  <section id={id} className="scroll-mt-28 pt-14 first:pt-0">
    <div className="flex items-baseline gap-3">
      {number && (
        <span className="font-display text-2xl text-sand-300 leading-none select-none">{number}</span>
      )}
      <h2 className="font-display text-3xl text-ink leading-tight">{title}</h2>
    </div>
    <div className="site-prose mt-5">{children}</div>
  </section>
);

/** Sub-heading inside a section. */
export const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-[1.0625rem] font-semibold text-ink mt-8 mb-2 first:mt-0">{children}</h3>
);

/** Emphasised statement — used for the principles that carry the system. */
export const Pull: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <blockquote className="my-7 border-l-2 border-accent pl-5 sm:pl-6">
    <p className="font-display text-xl sm:text-2xl leading-snug text-ink">{children}</p>
  </blockquote>
);

type NoteTone = "neutral" | "accent" | "caution";

const NOTE_TONES: Record<NoteTone, string> = {
  neutral: "bg-sand-100 border-sand-200",
  accent: "bg-accent-pale/60 border-accent/25",
  caution: "bg-amber-50 border-amber-200",
};

/** Aside box: a caveat, a limitation, or something easy to get wrong. */
export const Note: React.FC<{
  title?: string;
  tone?: NoteTone;
  children: React.ReactNode;
}> = ({ title, tone = "neutral", children }) => (
  <div className={`my-6 rounded-lg border px-5 py-4 ${NOTE_TONES[tone]}`}>
    {title && <p className="font-semibold text-ink mb-1.5">{title}</p>}
    <div className="site-prose text-[0.9375rem]">{children}</div>
  </div>
);

/** Bulleted list with a restrained marker. */
export const Bullets: React.FC<{ children: React.ReactNode; tight?: boolean }> = ({
  children,
  tight = false,
}) => (
  <ul className={`my-4 ${tight ? "space-y-1.5" : "space-y-2.5"}`}>{children}</ul>
);

export const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="relative pl-5 leading-relaxed text-[#3B4A48]">
    <span className="absolute left-0 top-[0.6em] h-1.5 w-1.5 rounded-full bg-accent/70" />
    {children}
  </li>
);

/** Definition list: term on the left, explanation on the right. */
export const DefList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dl className="my-6 divide-y divide-sand-200 border-y border-sand-200">{children}</dl>
);

export const DefItem: React.FC<{ term: React.ReactNode; children: React.ReactNode }> = ({
  term,
  children,
}) => (
  <div className="grid gap-1 py-4 sm:grid-cols-[11rem,1fr] sm:gap-6">
    <dt className="font-semibold text-ink">{term}</dt>
    <dd className="site-prose text-[0.9375rem]">{children}</dd>
  </div>
);

/** Two-column table for situation → response style content. */
export const SimpleTable: React.FC<{
  head: [string, string];
  rows: Array<[React.ReactNode, React.ReactNode]>;
}> = ({ head, rows }) => (
  <div className="my-6 overflow-x-auto">
    <table className="w-full min-w-[34rem] border-collapse text-left text-[0.9375rem]">
      <thead>
        <tr className="border-b border-sand-300">
          {head.map((h) => (
            <th key={h} className="pb-2 pr-6 font-semibold text-ink">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-sand-200 align-top">
            <td className="py-3 pr-6 font-medium text-ink-800">{row[0]}</td>
            <td className="py-3 text-[#3B4A48] leading-relaxed">{row[1]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Card used on the landing page and the section indexes. */
export const Card: React.FC<{
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ title, children, footer }) => (
  <div className="flex h-full flex-col rounded-xl border border-sand-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,46,46,0.04)]">
    <h3 className="font-display text-xl text-ink">{title}</h3>
    <div className="site-prose mt-3 flex-1 text-[0.9375rem]">{children}</div>
    {footer && <div className="mt-5 text-sm font-semibold text-accent">{footer}</div>}
  </div>
);

/** Status pill used on the roadmap. */
export const Pill: React.FC<{ tone: "done" | "now" | "next" | "later"; children: React.ReactNode }> = ({
  tone,
  children,
}) => {
  const tones = {
    done: "bg-accent-pale text-ink-800 border-accent/30",
    now: "bg-amber-50 text-amber-900 border-amber-200",
    next: "bg-sand-100 text-ink-600 border-sand-300",
    later: "bg-white text-ink-400 border-sand-200",
  } as const;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${tones[tone]}`}
    >
      {children}
    </span>
  );
};
